import { Switch, Route, useLocation, Redirect } from "wouter";
import { Sentry } from "./lib/sentry";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useRef } from "react";
import { identifyUser, track, trackPageview } from "@/lib/analytics";
import { CommandPaletteProvider } from "@/hooks/use-command-palette";
import Landing from "@/pages/landing";
import LoginPage from "@/pages/login";
import Dashboard from "@/pages/dashboard";
import Companies from "@/pages/companies";
import Carriers from "@/pages/carriers";
import CarrierDetailsPage from "@/pages/carrier-details";
import CompanyDetailsPage from "@/pages/company-details";
import WorkOrders from "@/pages/work-orders";
import WorkOrderDetailsPage from "@/pages/work-order-details";
import Documents from "@/pages/documents";
import Contacts from "@/pages/contacts";
import ContactDetailsPage from "@/pages/contact-details";
import UsersPage from "@/pages/users";
import ServiceTypes from "@/pages/service-types";
import CredentialTypesPage from "@/pages/credential-types";
import { ServiceRatesPage } from "@/pages/service-rates";
import { TaskTemplates } from "@/pages/task-templates";
import { ServiceTemplates } from "@/pages/service-templates";
import AuditTrailPage from "@/pages/audit-trail";
import ReportsPage from "@/pages/reports";
import SalesByBucketPage from "@/pages/reports/sales-by-bucket";
import OperationsReportPage from "@/pages/reports/operations";
import DocumentationReportPage from "@/pages/reports/documentation";
import BillingReportPage from "@/pages/reports/billing";
import ComplianceReportPage from "@/pages/reports/compliance";
import FmcsaIncidentsPage from "@/pages/fmcsa-incidents";
import AuthorityLettersPage from "@/pages/authority-letters";
import WatchlistsPage from "@/pages/watchlists";
import PassThroughFeesPage from "@/pages/pass-through-fees";
import BatchInvoicesPage from "@/pages/batch-invoices";
import InvoicesPage from "@/pages/invoices";
import PaymentsPage from "@/pages/payments";
import SettingsPage from "@/pages/settings";
import ConnectedAppsPage from "@/pages/connected-apps";
import { isSafeConnectedAppsPath } from "@shared/connectedAppsReturn";
import NotFound from "@/pages/not-found";
import ForgotPasswordPage from "@/pages/forgot-password";
import ResetPasswordPage from "@/pages/reset-password";
import AcceptInvitePage from "@/pages/accept-invite";
import PortalLoginPage from "@/pages/portal/login";
import PortalRequestPage from "@/pages/portal/request";
import PortalGuidePage from "@/pages/portal/guide";
import { PortalGuard } from "@/pages/portal/shared";

// PortalGuard-wrapped redirect for bare /portal and unknown /portal/* paths.
// Role outcomes: admin/staff → /, direct_client → 403, unconfigured service_bureau → not-configured screen,
// service_bureau OK → /portal/request.
function PortalRedirect() {
  return (
    <PortalGuard>
      {() => <Redirect to="/portal/request" />}
    </PortalGuard>
  );
}

function RootRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      setLocation("/dashboard");
    }
  }, [isAuthenticated, isLoading, setLocation]);

  if (isLoading || !isAuthenticated) {
    return <Landing />;
  }

  return null;
}

function LoginRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const next = new URLSearchParams(window.location.search).get("next");
      setLocation(next && isSafeConnectedAppsPath(next) ? next : "/");
    }
  }, [isAuthenticated, isLoading, setLocation]);

  if (isLoading || !isAuthenticated) {
    return <LoginPage />;
  }

  return null;
}

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const here = `${window.location.pathname}${window.location.search}`;
      if (isSafeConnectedAppsPath(here)) {
        setLocation(`/login?next=${encodeURIComponent(here)}`);
      } else {
        setLocation("/login");
      }
    }
  }, [isAuthenticated, isLoading, setLocation]);

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return null;
  }

  return <Component />;
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={RootRoute} />
      <Route path="/dashboard">{() => <ProtectedRoute component={Dashboard} />}</Route>
      <Route path="/login" component={LoginRoute} />
      <Route path="/forgot-password" component={ForgotPasswordPage} />
      <Route path="/reset-password" component={ResetPasswordPage} />
      <Route path="/accept-invite" component={AcceptInvitePage} />
      <Route path="/companies">{() => <ProtectedRoute component={Companies} />}</Route>
      <Route path="/companies/:id">{() => <ProtectedRoute component={CompanyDetailsPage} />}</Route>
      <Route path="/carriers">{() => <ProtectedRoute component={Carriers} />}</Route>
      <Route path="/carriers/:id">{() => <ProtectedRoute component={CarrierDetailsPage} />}</Route>
      <Route path="/work-orders">{() => <ProtectedRoute component={WorkOrders} />}</Route>
      <Route path="/work-orders/:id">{() => <ProtectedRoute component={WorkOrderDetailsPage} />}</Route>
      <Route path="/documents">{() => <ProtectedRoute component={Documents} />}</Route>
      <Route path="/contacts">{() => <ProtectedRoute component={Contacts} />}</Route>
      <Route path="/contacts/:id">{() => <ProtectedRoute component={ContactDetailsPage} />}</Route>
      <Route path="/users">{() => <ProtectedRoute component={UsersPage} />}</Route>
      <Route path="/service-types">{() => <ProtectedRoute component={ServiceTypes} />}</Route>
      <Route path="/credential-types">{() => <ProtectedRoute component={CredentialTypesPage} />}</Route>
      <Route path="/service-rates">{() => <ProtectedRoute component={ServiceRatesPage} />}</Route>
      <Route path="/task-templates">{() => <ProtectedRoute component={TaskTemplates} />}</Route>
      <Route path="/service-templates">{() => <ProtectedRoute component={ServiceTemplates} />}</Route>
      <Route path="/fmcsa-incidents">{() => <ProtectedRoute component={FmcsaIncidentsPage} />}</Route>
      <Route path="/authority-letters">{() => <ProtectedRoute component={AuthorityLettersPage} />}</Route>
      <Route path="/watchlists">{() => <ProtectedRoute component={WatchlistsPage} />}</Route>
      <Route path="/audit-trail">{() => <ProtectedRoute component={AuditTrailPage} />}</Route>
      <Route path="/reports">{() => <ProtectedRoute component={ReportsPage} />}</Route>
      <Route path="/reports/sales-by-bucket">{() => <ProtectedRoute component={SalesByBucketPage} />}</Route>
      <Route path="/reports/operations">{() => <ProtectedRoute component={OperationsReportPage} />}</Route>
      <Route path="/reports/documentation">{() => <ProtectedRoute component={DocumentationReportPage} />}</Route>
      <Route path="/reports/billing">{() => <ProtectedRoute component={BillingReportPage} />}</Route>
      <Route path="/reports/compliance">{() => <ProtectedRoute component={ComplianceReportPage} />}</Route>
      <Route path="/pass-through-fees">{() => <ProtectedRoute component={PassThroughFeesPage} />}</Route>
      <Route path="/batch-invoices">{() => <ProtectedRoute component={BatchInvoicesPage} />}</Route>
      <Route path="/invoices">{() => <ProtectedRoute component={InvoicesPage} />}</Route>
      <Route path="/payments">{() => <ProtectedRoute component={PaymentsPage} />}</Route>
      <Route path="/settings">{() => <ProtectedRoute component={SettingsPage} />}</Route>
      <Route path="/connected-apps">{() => <ProtectedRoute component={ConnectedAppsPage} />}</Route>
      <Route path="/portal/login" component={PortalLoginPage} />
      <Route path="/portal/request" component={PortalRequestPage} />
      <Route path="/portal/guide" component={PortalGuidePage} />
      <Route path="/portal" component={PortalRedirect} />
      <Route path="/portal/:rest*" component={PortalRedirect} />
      <Route path="/:rest*" component={NotFound} />
    </Switch>
  );
}

function AnalyticsTracker() {
  const [location] = useLocation();
  const { isAuthenticated, isLoading, user } = useAuth();
  const lastPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (isLoading || location === lastPathRef.current) return;

    trackPageview(location, {
      source_page: location,
      is_authenticated: isAuthenticated,
    });

    if (location === "/dashboard") {
      track("dashboard_viewed", {
        source_page: location,
      });
    }

    lastPathRef.current = location;
  }, [location, isAuthenticated, isLoading]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user?.id) return;

    identifyUser({
      id: user.id,
      email: user.email,
      companyId: user.companyId,
      authType: user.authType,
      isActive: user.isActive,
    });
  }, [isAuthenticated, isLoading, user]);

  return null;
}

function ErrorFallback({ error, resetError }: { error: unknown; componentStack: string; eventId: string; resetError: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
        <p className="text-muted-foreground mb-4">
          An unexpected error occurred. The error has been reported automatically.
        </p>
        <button
          onClick={resetError}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <CommandPaletteProvider>
            <AnalyticsTracker />
            <Toaster />
            <Router />
          </CommandPaletteProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
